import { Lot } from '../../../manage/shared/models'
import { LotActivityStatus } from '../../../../core/constants'
import { StringHelper } from './StringHelper'
import { START_PRICE_TYPE } from '../constants/constants'
import { LotType } from '../../../../core/constants/lot-type'
import objectPath from 'object-path'
import { LotMediaAdded } from '../models/lot-media-added'
import { MediaType } from '../models/media-type'
import { MetaType } from '../models/auction-media'
import { LotOffer } from '../../../../core/models'
import { OfferHelper } from './OfferHelper'
import { LotStatus, LotStatusUpdatePayload } from '../../../../core/models/hub-client'
import { EAuctionLotLabel } from 'src/_config/enumerations'

export class LotHelper {
    static getStatusName(stateNumber: number) {
        return StringHelper.camelCaseToSpaces(
            stateNumber
                ? Object.keys(LotActivityStatus)
                      .map(key => ({ id: Number(key), name: LotActivityStatus[key] }))
                      .find(x => x.id === stateNumber)!.name
                : ''
        )
    }

    static isLotNotConfirmed(lot: Lot) {
        //does not have current and has upcoming???
        return !lot
    }

    static setLotLabel(auctionLotLabel: EAuctionLotLabel | null) {
        switch (auctionLotLabel) {
            case EAuctionLotLabel.Saleyard:
                return 'pen'
            case EAuctionLotLabel.OnFarmHybrid:
                return 'lot'
            case EAuctionLotLabel.HundredPercentOnline:
                return 'lot'
            default:
                return 'lot'
        }
    }

    // This is related to the lot activity
    static isLotSelling(lot: Lot) {
        return lot && [LotActivityStatus.NowSelling].includes(lot.activityStatus!)
    }

    //this is used to display column with setting price / bid / increments controls
    // so should be displayed only in following states
    static isLotOngoing(lot: Lot) {
        return (
            lot &&
            [LotActivityStatus.NowSelling, LotActivityStatus.Started, LotActivityStatus.Paused, LotActivityStatus.ResumeDelayed, LotActivityStatus.StartDelayPaused].includes(
                lot.activityStatus!
            )
        )
    }

    // This is related to the 'Now Selling' flag that is displayed to the buyers
    static isLotNowSelling(lot: Lot) {
        return lot && lot.nowSelling
    }

    static isLotFinished(lot: Lot) {
        return lot && [LotActivityStatus.FinishDelayed].includes(lot.activityStatus!)
    }

    static isLotClosing(lot: Lot) {
        return lot && lot.lotClosing
    }

    static isLotClosed(lot: Lot) {
        return lot && [LotActivityStatus.Sold, LotActivityStatus.NotSold, LotActivityStatus.PassedIn, LotActivityStatus.SoldPostAuction].includes(lot.activityStatus!)
    }

    static isLotSold(lot: Lot) {
        return lot && [LotActivityStatus.Sold].includes(lot.activityStatus!)
    }

    static isLotNotSold(lot: Lot) {
        return lot && [LotActivityStatus.NotSold].includes(lot.activityStatus!)
    }

    static isLotPassedIn(lot: Lot) {
        return lot && [LotActivityStatus.PassedIn].includes(lot.activityStatus!)
    }

    static isLotSoldOrPostAuctionSold(lot: Lot) {
        return lot && [LotActivityStatus.Sold, LotActivityStatus.SoldPostAuction].includes(lot.activityStatus!)
    }

    static isStarted(lot: Lot) {
        return lot && [LotActivityStatus.Started].includes(lot.activityStatus!)
    }

    static isUpcoming(lot: Lot) {
        return lot && [LotActivityStatus.Active, LotActivityStatus.Withdrawn].includes(lot.activityStatus!)
    }

    static isActive(lot: Lot) {
        return lot && [LotActivityStatus.Active].includes(lot.activityStatus!)
    }

    static isWithdrawn(lot: Lot) {
        return lot && [LotActivityStatus.Withdrawn].includes(lot.activityStatus!)
    }

    static isOfferManagement(lot: Lot) {
        return lot && !LotHelper.isSingleNormalLot(lot) && [LotActivityStatus.OfferManagement].includes(lot.activityStatus!)
    }

    static isInOfferState(lot: Lot | null) {
        return (lot && LotHelper.isOfferManagement(lot)) || LotHelper.isUnderOffer(lot)
    }

    static isUnderOffer(lot: Lot | null) {
        return lot && !LotHelper.isSingleNormalLot(lot) && [LotActivityStatus.UnderOffer].includes(lot.activityStatus!)
    }

    static isUnderOfferByFloorBidder(lot: Lot | null, floorBidderId: number | null | undefined, offer: LotOffer | null | undefined) {
        return LotHelper.isUnderOffer(lot) && offer && offer.auctionBidderId && offer.auctionBidderId === floorBidderId
    }

    //edit rules
    static canBeModified(lot: Lot) {
        return (
            lot &&
            [
                LotActivityStatus.Started,
                LotActivityStatus.NowSelling,
                LotActivityStatus.Paused,
                LotActivityStatus.Active, // can also edit upcoming lot
            ].includes(lot.activityStatus!)
        )
    }

    static canWithdrawBid(lot: Lot) {
        return (
            lot &&
            [
                LotActivityStatus.NowSelling,
                LotActivityStatus.Paused,
                LotActivityStatus.ResumeDelayed,
                LotActivityStatus.FinishDelayed,
                LotActivityStatus.NotSold,
                LotActivityStatus.Sold,
            ].includes(lot.activityStatus!)
        )
    }

    static isInStartPriceMode(lot: Lot) {
        return lot && lot.initialPriceType === START_PRICE_TYPE.START_PRICE
    }

    static isInCallPriceMode(lot: Lot) {
        return lot && lot.initialPriceType === START_PRICE_TYPE.CALL_PRICE
    }

    static canStartLot(lot: Lot) {
        return lot && (Number(lot.increment) > 0 || Number(lot.pendingIncrement) > 0)
    }

    static filterByLotNumber(l: Lot, filter: string) {
        const { lotNumber, bidder, purchaserName, title } = l
        let propsToSeach = [lotNumber, bidder, purchaserName]
        if (LotHelper.isPick(l)) {
            propsToSeach.push(title)
        }
        const textToSearch = propsToSeach.join(' ').toLocaleLowerCase()
        return textToSearch.indexOf(filter) !== -1
    }

    static isSingleNormalLot(l: Lot) {
        return l && !LotHelper.isPick(l) && !LotHelper.isRunout(l)
    }

    static isIndividualLot(l: Lot) {
        return l && !LotHelper.hasParent(l)
    }

    static isPick(l: Lot) {
        return (
            l &&
            l.lotType === LotType.Pick &&
            //check on both with OR
            (Number(l.parentId) < 1 || (Array.isArray(l.childIds) && l.childIds.length > 0))
        )
    }

    static isChildPick(l: Lot) {
        return l && l.lotType === LotType.Pick && Number(l.parentId) > 0
    }

    static isRunout(l: Lot | null) {
        return l && l.lotType === LotType.Runout
    }

    static isParentRunoutWithNoTally(l: Lot) {
        return l && l.lotType === LotType.Runout && Number(l.parentId) < 1 && Number(l.tally) === 0
    }

    static isRunoutByType(type: LotType | undefined) {
        return type === LotType.Runout
    }

    static getChildLots(l: Lot, lots: Lot[]) {
        let children: Lot[] = []
        if (!LotHelper.isSingleNormalLot(l) && Array.isArray(lots)) {
            children = lots.filter(x => l.id === x.parentId)
        }
        return children.sort((a, b) => a.order! - b.order!)
    }

    static parentIsUpcoming(l: Lot, lots: Lot[]) {
        if (l && Array.isArray(lots)) {
            let parent = lots.filter(x => x.id == l.parentId && LotHelper.isUpcoming(x))
            return parent.length > 0
        }
        return
    }

    static getPickLiveTitle(childIds: number[], lots: Lot[]) {
        let pickChildren: Lot[] = lots.filter(x => childIds.includes(x.id!))
        return pickChildren.length > 0 ? 'Lots ' + pickChildren.map(x => x.lotNumber).join(', ') : ''
    }

    static getIconByType(l: Lot, color: string = 'white') {
        if (LotHelper.isPick(l)) {
            return ['pick', color].join('-')
        } else if (LotHelper.isRunout(l)) {
            return ['runs', color].join('-')
        }
        return null
    }

    static hasParent(l: any) {
        return Number(objectPath.get(l, 'parentId', 0)) > 0
    }

    static getParent(l: any, lots: Lot[]) {
        var parent = lots.find(x => x.id === Number(objectPath.get(l, 'parentId', 0)))
        return parent ? parent : null
    }

    static getParentNumber(parentId: number, lots: Lot[]) {
        var parent = lots.find(x => x.id === parentId)
        return parent ? parent.lotNumber : ''
    }

    static canAddPick(lots: Lot[]) {
        if (Array.isArray(lots)) {
            return lots.filter(x => !LotHelper.hasParent(x) && LotHelper.canBePartOfPickGroup(x)).length > 1
        }
        return false
    }

    static getCurrentLotInfoHeading(l: Lot) {
        if (LotHelper.isPick(l)) {
            return 'Selected Pick'
        } else if (LotHelper.isRunout(l)) {
            return 'Selected lot'
        }
        return 'Selected lot'
    }

    static getLabelByType(l: Lot) {
        if (LotHelper.isPick(l)) {
            return LotHelper.isInOfferState(l) ? 'Pick Offer' : 'Pick'
        } else if (LotHelper.isRunout(l)) {
            return LotHelper.isInOfferState(l) ? 'Runout Offer' : 'Runout'
        }
        return 'Lot'
    }

    static isChildAvailableForOffer(l: Lot) {
        return l && ![LotActivityStatus.Withdrawn, LotActivityStatus.Sold].includes(l.activityStatus!)
    }

    static mapUpdateMediaPayload(mediaMeta: LotMediaAdded) {
        let mappedMedia = {
            isPhoto: mediaMeta.MediaType === MediaType.Image && mediaMeta.MetaType === MetaType.None,
            isVideo: mediaMeta.MediaType === MediaType.Video,
        }

        //modify uppercase that comes from server
        Object.keys(mediaMeta).forEach(key => {
            let lowercaseKey = key.charAt(0).toLowerCase() + key.slice(1)
            if (typeof mediaMeta[key] === 'object' && mediaMeta[key] !== null) {
                let innerObject = {}
                Object.keys(mediaMeta[key]).forEach(innerObjectKey => {
                    let lowercaseSizeKey = innerObjectKey.charAt(0).toLowerCase() + innerObjectKey.slice(1)
                    innerObject[lowercaseSizeKey] = mediaMeta[key][innerObjectKey]
                })
                mappedMedia[lowercaseKey] = innerObject
            } else {
                mappedMedia[lowercaseKey] = mediaMeta[key]
            }
        })

        return mappedMedia
    }

    static getStatusFromParent(model: any, currentLot: Lot) {
        return (
            LotHelper.isPick(currentLot) &&
            model &&
            model.parentId == currentLot.id &&
            //not sure about this
            ![LotActivityStatus.NotSold, LotActivityStatus.PassedIn, LotActivityStatus.Sold, LotActivityStatus.Withdrawn].includes(model.activityStatus)
        )
    }

    static getCurrentOffer(currentLot: Lot | null) {
        if (LotHelper.isInOfferState(currentLot) && Array.isArray(currentLot!.offers)) {
            //get last active offer
            return currentLot!.offers.sort((a, b) => a.id - b.id).find(o => OfferHelper.isOfferActive(o))
        }
        return null
    }

    static getOffersByLotBiddingRun(currentLot: Lot) {
        if (LotHelper.isInOfferState(currentLot) && currentLot.biddingRun && Array.isArray(currentLot.offers)) {
            return currentLot.offers.filter(o => o.biddingRun === currentLot.biddingRun)
        }
        return []
    }

    static isUnderOfferToBidderById(currentLot: Lot, bidderId: number) {
        let currentOffer = LotHelper.getCurrentOffer(currentLot)
        return currentOffer && currentOffer.auctionBidderId === bidderId
    }

    static canBeSelectedForPickOffer(lot: Lot) {
        return LotHelper.isActive(lot) && !LotHelper.isWithdrawn(lot) && !LotHelper.isLotSoldOrPostAuctionSold(lot)
    }

    static canBePartOfPickGroup(lot: Lot) {
        return LotHelper.isUpcoming(lot) && LotHelper.isSingleNormalLot(lot)
    }

    static hasAnyLotsSoldInCurrentBiddingRun(lot: Lot) {
        if (LotHelper.isPick(lot)) {
            let offers = LotHelper.getOffersByLotBiddingRun(lot)
            if (offers.filter(o => Array.isArray(o.acceptedLotIds) && o.acceptedLotIds.length).length) {
                return true
            }
        }
        return false
    }

    static getPickChildPrice(lot: Lot, currentLot: Lot) {
        if (!LotHelper.isWithdrawn(lot)) {
            if (LotHelper.isLotSoldOrPostAuctionSold(lot)) {
                return lot.soldPrice
            }
            //return offer price
            if (lot && LotHelper.isUnderOffer(currentLot) && currentLot.id === lot.parentId) {
                let offer = LotHelper.getCurrentOffer(currentLot)
                if (offer) {
                    return offer.amount
                }
            }
        }
        return null
    }

    static mapLotStatusToActivityStatus(status: LotStatus): LotActivityStatus | undefined | null {
        switch (status) {
            case LotStatus.Started:
                return LotActivityStatus.Started
                break
            case LotStatus.NowSelling:
                return LotActivityStatus.NowSelling
                break
            case LotStatus.Sold:
                return LotActivityStatus.Sold
                break
            case LotStatus.NotSold:
                return LotActivityStatus.NotSold
                break
            case LotStatus.PassedIn:
                return LotActivityStatus.PassedIn
                break
            case LotStatus.WithdrawnNotSold:
                return LotActivityStatus.WithdrawnNotSold
                break
            case LotStatus.FinishDelayed:
                return LotActivityStatus.FinishDelayed
                break
            case LotStatus.OfferManagement:
                return LotActivityStatus.OfferManagement
                break
            case LotStatus.UnderOffer:
                return LotActivityStatus.UnderOffer
                break
            case LotStatus.Removed:
                return LotActivityStatus.Removed
                break
            default:
                return null
                break
        }
    }

    static isFirstBiddingRun(lot: Lot) {
        return lot && lot.biddingRun === 1
    }

    static availableForOffer(lot: Lot, childLots: Lot[]) {
        return (
            !LotHelper.isUnderOffer(lot) && // is not under offer
            (LotHelper.hasAvailablePickChildrenForSale(lot, childLots) || LotHelper.hasAvailableTallyForSale(lot))
        )
    }

    static hasAvailablePickChildrenForSale(lot: Lot, childLots: Lot[]) {
        if (LotHelper.isPick(lot)) {
            return Array.isArray(childLots) && childLots.findIndex(l => LotHelper.canBeSelectedForPickOffer(l) && l.parentId === lot.id) !== -1
        }
        return false
    }

    static hasAvailableTallyForSale(lot: Lot) {
        if (LotHelper.isRunout(lot)) {
            //in case all sold in one offer for runout we are not reducing tally and marking that parent lot as sold
            let firstAcceptedOffer = Array.isArray(lot.offers) ? lot.offers.find(x => x.acceptedTally! > 0) : null
            let isFirstOffer = Array.isArray(lot.offers) && lot.offers.filter(o => o.acceptedTally! > 0).length === 1
            //is runout and tally more than 0
            return Number(lot.tally) > 0 && !(isFirstOffer && firstAcceptedOffer && firstAcceptedOffer.availableTally === firstAcceptedOffer.acceptedTally)
        }

        return false
    }

    static getRunoutMinimumValidTally(lot: Lot) {
        return LotHelper.isRunout(lot) && Number(lot.runoutMinimumTally! | 0) > 0 ? Math.min(Number(lot.tally! | 0), Number(lot.runoutMinimumTally! | 0)) : 0
    }

    static getValidatedSelectedTally(lot: Lot, newValue: number) {
        let isLessThanTally = lot.tally! >= newValue
        let isMoreThanMinTally = newValue >= LotHelper.getRunoutMinimumValidTally(lot)

        // reset if does not pass
        return isLessThanTally && isMoreThanMinTally ? newValue : 0
    }

    static isSoldNotToFloorBidder(lot: Lot, floorBidderNumber) {
        return LotHelper.isLotSoldOrPostAuctionSold(lot) && lot.bidder && Number(lot.bidder) !== floorBidderNumber
    }

    static isSelecteTallyValidForSubmission(tally: number, selectedOfferTally: number) {
        return selectedOfferTally > 0 && selectedOfferTally <= tally
    }

    static isTallyDefinedOnPayload(payload: LotStatusUpdatePayload) {
        return payload && typeof payload.Tally !== 'undefined' && payload.Tally !== null // 0 is valid value
    }

    static getRunoutRemainingTally(lot: Lot | null, offer: LotOffer | null | undefined) {
        if (!lot || !LotHelper.isRunout(lot)) {
            return 0
        }

        if (offer && typeof offer.availableTally === 'number') {
            return offer.availableTally
        }

        const acceptedTally = Array.isArray(lot.offers)
            ? lot.offers.reduce((total, currentOffer) => total + (currentOffer?.acceptedTally ?? 0), 0)
            : 0

        const remaining = (lot.tally ?? 0) - acceptedTally
        return remaining > 0 ? remaining : 0
    }
}
